import React, { useState, useEffect } from "react";
import { connect } from "react-redux";
import { getDataFromAPI } from "../../../../HandleAPICalls/actions";
import { toastr } from "react-redux-toastr";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import AddCorporateUserComponent from "./component";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Box,
  Typography
} from "@mui/material";
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';

const AddCorporateUserContainer = ({ getDataFromAPI }) => {
  const { corporateId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const queryParams = new URLSearchParams(location.search);
  const editUserId = queryParams.get('edit');
  const editUserEmail = queryParams.get('email');

  const [singleUser, setSingleUser] = useState({
    employeeName: "",
    employeeId: "",
    employeeEmail: "",
    departmentId: "",
    departmentName: ""
  });

  const [openBulkModal, setOpenBulkModal] = useState(false);
  const [bulkFile, setBulkFile] = useState(null);
  const [parsedBulkUsers, setParsedBulkUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showResultPopup, setShowResultPopup] = useState(false);
  const [popupData, setPopupData] = useState({
    type: "success",
    message: "",
    addedCount: 0,
    failedCount: 0
  });
  const [departments, setDepartments] = useState([]);
  const [isEditMode, setIsEditMode] = useState(false);
  const [userHasDepartment, setUserHasDepartment] = useState(false);
  const [existingDepartment, setExistingDepartment] = useState("");
  const [departmentOptions, setDepartmentOptions] = useState([]);
  const [onlyDefaultDepartment, setOnlyDefaultDepartment] = useState(false);

  // Check if user has department in localStorage
  const checkUserDepartment = (employeeId) => {
    try {
      const storageKey = `corporate_${corporateId}_users`;
      const savedUsers = localStorage.getItem(storageKey);
      
      if (savedUsers) {
        const parsedUsers = JSON.parse(savedUsers);
        const foundUser = parsedUsers.find(user => 
          user.usersCorporateId === employeeId
        );
        
        if (foundUser && 
            foundUser.departmentName && 
            foundUser.departmentName.trim() !== "" && 
            foundUser.departmentName.toLowerCase() !== "default" &&
            foundUser.departmentName.toLowerCase() !== "not assigned") {
          return foundUser.departmentName;
        }
      }
      
      return null;
    } catch (error) {
      console.error("Error checking user department:", error);
      return null;
    }
  };

  // Load user data and check department
  useEffect(() => {
    if (corporateId) {
      const storageKey = `corporate_${corporateId}_users`;
      try {
        const savedUsers = localStorage.getItem(storageKey);
        if (savedUsers) {
          const parsedUsers = JSON.parse(savedUsers);
          
          if (editUserId || editUserEmail) {
            setIsEditMode(true);
            
            const userToEdit = parsedUsers.find(user => 
              (editUserId && user.usersCorporateId === editUserId) || 
              (editUserEmail && user.emailId === editUserEmail)
            );
            
            if (userToEdit) {
              setSingleUser({
                employeeName: userToEdit.userName || "",
                employeeId: userToEdit.usersCorporateId || "",
                employeeEmail: userToEdit.emailId || "",
                departmentName: userToEdit.departmentName || "",
                departmentId: ""
              });
              
              const userDept = checkUserDepartment(userToEdit.usersCorporateId);
              
              if (userDept) {
                setUserHasDepartment(true);
                setExistingDepartment(userDept);
                
                if (departmentOptions.length > 0) {
                  const deptObj = departmentOptions.find(d => d.name === userDept);
                  if (deptObj) {
                    setSingleUser(prev => ({
                      ...prev,
                      departmentId: deptObj.id.toString()
                    }));
                  }
                }
              } else {
                setUserHasDepartment(false);
                setExistingDepartment("");
              }
            }
          }
        }
      } catch (error) {
        console.error("Failed to load user data:", error);
      }
    }
  }, [corporateId, editUserId, editUserEmail, departmentOptions]);

  // Fetch departments from API
  useEffect(() => {
    fetchDepartments();
  }, [corporateId]);

  const fetchDepartments = () => {
    getDataFromAPI(
      `/partner/api/v2/corporate/${corporateId}/departments`,
      "GET",
      undefined,
      (response) => {
        let allDepartments = [];
        let hasOnlyDefault = false;
        
        if (response && response && Array.isArray(response)) {
          allDepartments = response.map(dept => ({
            id: dept.departmentId,
            name: dept.departmentName
          }));
        } else if (response && Array.isArray(response)) {
          allDepartments = response.map(dept => ({
            id: dept.departmentId || dept.id,
            name: dept.departmentName || dept.name
          }));
        }
        
        const nonDefaultDepartments = allDepartments.filter(dept => 
          dept.name && dept.name.toLowerCase() !== "default"
        );
        
        hasOnlyDefault = allDepartments.length > 0 && nonDefaultDepartments.length === 0;
        
        const departmentOptions = hasOnlyDefault 
          ? allDepartments
          : allDepartments.filter(dept => dept.name.toLowerCase() !== "default");
        
        setDepartments(departmentOptions);
        setDepartmentOptions(departmentOptions);
        setOnlyDefaultDepartment(hasOnlyDefault);
      },
      (err) => {
        console.error("Failed to fetch departments:", err);
        setDepartments([]);
        setDepartmentOptions([]);
        setOnlyDefaultDepartment(false);
      }
    );
  };

  // Download CSV Template
  const downloadCSVTemplate = () => {
    const csvContent = "data:text/csv;charset=utf-8," +
      "Employee Name,Employee ID,Employee Email,Department ID\n" +
      "Test User,EMP010,test@test.com,4\n" +
      "John Doe,EMP011,john@test.com,3";
    
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", "corporate_users_template.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Handle Bulk File Upload
  const handleBulkFileUpload = (file) => {
    setBulkFile(file);
    const reader = new FileReader();

    reader.onload = (e) => {
      try {
        const csvText = e.target.result;
        const rows = csvText.split("\n").filter((line) => line.trim() !== "");
        
        if (rows.length < 2) {
          toastr.error("Error", "CSV file is empty or has no data rows");
          return;
        }

        const parsed = rows.slice(1).map((row, index) => {
          const values = row.split(",").map((v) => v.trim());
          
          return {
            employeeId: values[1] || "",
            employeeName: values[0] || "",
            employeeEmail: values[2] || "",
            departmentId: values[3] ? parseInt(values[3]) : null
          };
        }).filter(user => 
          user.employeeId && 
          user.employeeName && 
          user.employeeEmail
        );

        setParsedBulkUsers(parsed);
        toastr.success("Success", `Found ${parsed.length} valid users`);
      } catch (error) {
        console.error("CSV parsing error:", error);
        toastr.error("Error", "Failed to parse CSV file. Please check the format.");
      }
    };

    reader.onerror = () => toastr.error("Error", "Failed to read file");
    reader.readAsText(file);
  };

  // Handle Bulk Add User
  const handleBulkAddUser = () => {
    if (parsedBulkUsers.length === 0) {
      toastr.error("Error", "No valid users to upload");
      return;
    }

    setIsLoading(true);

    const payload = {
      userInfoList: parsedBulkUsers
    };

    getDataFromAPI(
      `/partner/api/v1/business-profile/${corporateId}`,
      "POST",
      payload,
      (res) => {
        setIsLoading(false);
        
        const successes = res?.successes || [];
        const failures = res?.failures || [];
        
        const successCount = successes.length;
        const failureCount = failures.length;
        
        if (successCount > 0 && failureCount === 0) {
          setPopupData({
            type: "success",
            message: "Users added successfully!",
            addedCount: successCount,
            failedCount: 0
          });
          setShowResultPopup(true);
          // Close modal and clear data after popup shows
          setParsedBulkUsers([]);
          setBulkFile(null);
          setOpenBulkModal(false);
        } else if (successCount > 0 && failureCount > 0) {
          setPopupData({
            type: "partial",
            message: `${successCount} user(s) added, ${failureCount} user(s) failed`,
            addedCount: successCount,
            failedCount: failureCount
          });
          setShowResultPopup(true);
          setParsedBulkUsers([]);
          setBulkFile(null);
          setOpenBulkModal(false);
        } else if (failureCount > 0 && successCount === 0) {
          setPopupData({
            type: "error",
            message: failures[0]?.reason || "Failed to add users",
            addedCount: 0,
            failedCount: failureCount
          });
          setShowResultPopup(true);
          setParsedBulkUsers([]);
          setBulkFile(null);
          setOpenBulkModal(false);
        }
      },
      (err) => {
        setIsLoading(false);
        
        let errorMessage = "Failed to add users";
        
        if (err?.message) {
          errorMessage = err.message;
        } else if (err?.response?.message) {
          errorMessage = err.response?.message;
        } else if (err?.status?.message) {
          errorMessage = err.status.message;
        }
        
        setPopupData({
          type: "error",
          message: errorMessage,
          addedCount: 0,
          failedCount: parsedBulkUsers.length
        });
        setShowResultPopup(true);
        setParsedBulkUsers([]);
        setBulkFile(null);
        setOpenBulkModal(false);
      }
    );
  };

  // Handle Create New User (Single User)
  const handleCreateNewUser = () => {
    if (!singleUser.employeeName.trim()) {
      toastr.error("Error", "Please enter employee name");
      return;
    }
    if (!singleUser.employeeId.trim()) {
      toastr.error("Error", "Please enter employee ID");
      return;
    }
    if (!singleUser.employeeEmail.trim()) {
      toastr.error("Error", "Please enter employee email");
      return;
    }
    
    if (!isEditMode && !onlyDefaultDepartment && !singleUser.departmentId) {
      toastr.error("Error", "Please select a department");
      return;
    }
    
    const selectedDept = departments.find(dept => 
      dept.name === singleUser.departmentName || dept.id.toString() === singleUser.departmentId
    );
    
    const userPayload = {
      employeeId: singleUser.employeeId,
      employeeName: singleUser.employeeName,
      employeeEmail: singleUser.employeeEmail,
      departmentId: selectedDept ? selectedDept.id : null
    };

    const payload = {
      userInfoList: [userPayload]
    };

    setIsLoading(true);

    getDataFromAPI(
      `/partner/api/v1/business-profile/${corporateId}`,
      "POST",
      payload,
      (res) => {
        setIsLoading(false);
        
        const successes = res?.successes || [];
        const failures = res?.failures || [];
        
        const hasSuccess = successes && successes.length > 0;
        const hasFailure = failures && failures.length > 0;
        
        if (hasSuccess && !hasFailure) {
          toastr.success("Success", isEditMode ? "User updated successfully" : "User created successfully");
          setSingleUser({
            employeeName: "",
            employeeId: "",
            employeeEmail: "",
            departmentId: "",
            departmentName: ""
          });
          navigate(-1);
        } else if (hasFailure) {
          const errorMessage = failures[0]?.reason || "Failed to create user";
          toastr.error("Error", errorMessage);
        } else {
          toastr.error("Error", "No response data received");
        }
      },
      (err) => {
        setIsLoading(false);
        
        let errorMessage = "Failed to create user";
        
        if (err?.message) {
          errorMessage = err.message;
        } else if (err?.response?.message) {
          errorMessage = err.response?.message;
        } else if (err?.status?.message) {
          errorMessage = err.status.message;
        }
        
        toastr.error("Error", errorMessage);
      }
    );
  };

  const handleClosePopup = () => {
    setShowResultPopup(false);
    // Only navigate back on success or partial success, not on error
    if (popupData.type !== 'error') {
      navigate(-1);
    }
  };

  return (
    <>
      <AddCorporateUserComponent
        singleUser={singleUser}
        setSingleUser={setSingleUser}
        handleCreateNewUser={handleCreateNewUser}
        openBulkModal={openBulkModal}
        setOpenBulkModal={setOpenBulkModal}
        handleBulkFileUpload={handleBulkFileUpload}
        parsedBulkUsers={parsedBulkUsers}
        handleBulkAddUser={handleBulkAddUser}
        downloadCSVTemplate={downloadCSVTemplate}
        setParsedBulkUsers={setParsedBulkUsers}
        bulkFile={bulkFile}
        setBulkFile={setBulkFile}
        departments={departments}
        departmentOptions={departmentOptions}
        isLoading={isLoading}
        isEditMode={isEditMode}
        corporateId={corporateId}
        userHasDepartment={userHasDepartment}
        existingDepartment={existingDepartment}
        onlyDefaultDepartment={onlyDefaultDepartment}
      />

      {/* Result Popup */}
      <Dialog
        open={showResultPopup}
        onClose={handleClosePopup}
        PaperProps={{
          sx: {
            borderRadius: '20px',
            padding: '24px',
            minWidth: '350px',
            textAlign: 'center',
            position: 'relative',
            zIndex: 1300
          }
        }}
        BackdropProps={{
          sx: {
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            backdropFilter: 'blur(4px)'
          }
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
          {popupData.type === 'success' && (
            <CheckCircleIcon sx={{ fontSize: 60, color: '#4caf50' }} />
          )}
          {popupData.type === 'partial' && (
            <Box sx={{ position: 'relative' }}>
              <CheckCircleIcon sx={{ fontSize: 60, color: '#ff9800' }} />
            </Box>
          )}
          {popupData.type === 'error' && (
            <ErrorIcon sx={{ fontSize: 60, color: '#f44336' }} />
          )}
        </Box>
        
        <DialogTitle sx={{ pb: 1, fontWeight: 600, fontSize: '24px' }}>
          {popupData.type === 'success' && 'Success!'}
          {popupData.type === 'partial' && 'Partial Success'}
          {popupData.type === 'error' && 'Error!'}
        </DialogTitle>
        
        <DialogContent>
          <DialogContentText sx={{ mb: 2, fontSize: '16px' }}>
            {popupData.message}
          </DialogContentText>
          
          {/* {popupData.addedCount > 0 && (
            <Typography variant="body1" sx={{ color: '#4caf50', fontWeight: 500 }}>
              ✅ {popupData.addedCount} user(s) added successfully
            </Typography>
          )} */}
          
          {/* {popupData.failedCount > 0 && (
            <Typography variant="body1" sx={{ color: '#f44336', fontWeight: 500, mt: 1 }}>
              ❌ {popupData.failedCount} user(s) failed
            </Typography>
          )} */}
        </DialogContent>
        
        <DialogActions sx={{ justifyContent: 'center', pb: 2 }}>
          <Button
            onClick={handleClosePopup}
            variant="contained"
            sx={{
              backgroundColor: '#14AE78',
              borderRadius: '30px',
              px: 4,
              py: 1,
              textTransform: 'none',
              '&:hover': {
                backgroundColor: '#0e9c68'
              }
            }}
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default connect(null, { getDataFromAPI })(AddCorporateUserContainer);